import React, { useEffect, useRef, useState } from 'react';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { AppBar, Box, Button, createTheme, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useApi } from '../../../../services/HttpService';
import ScrollableDataGrid from '../../../../components/tables/ScrollableDataGrid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import BaseModal from '../../../../components/modals/BaseModal';
import { MemberForm, MemberFormRef } from '../../../../model/forms/MemberForm';
import { StickyAlert } from '../../../../components/ui/StickyAlert';
import { highlightedGridColumns } from '../../../../components/tables/BaseTable';
import { EnhancedTableToolbar } from '../../../../components/tables/AdminTable';
import HardDeleteDialog from '../../../../components/modals/dialog/HardDeleteDialog';
import { PaymentprofileOverview } from './PaymentprofileOverview';
import {newPaymentprofile, Paymentprofile, PrepaymentSolution, newPrepaymentSolution, ErpSolution ,newErpSolution} from '../../../../model/PaymentProfile';

const mainColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Navn', width: 160 },
    { field: 'post_ordering', headerName: 'Post bestilling', width: 160 , type: 'boolean'},
    { field: 'salary_deduction', headerName: 'Lønnstrekk', width: 160 , type: 'boolean'},
    { field: 'order_prepayment_solution_id', headerName: 'Forskuddsbetlaing ID', width: 200 },
    { field: 'order_erp_solution_id', headerName: 'Erp ID', width: 160 },
    { field: 'external_id', headerName: 'Ekstern ID', width: 130 },
    { field: 'available_options_for_change', headerName: 'Endrings kriterier', width: 130 },
];

export const ManagePaymentprofiles = () => {
  const [paymentprofiles, setPaymentprofiles] = useState<Paymentprofile[]>([]);
  const [PrepaymentSolutions, setPrepaymentSolutions] = useState<PrepaymentSolution[]>([]);
  const [ErpSolutions, setErpSolutions] = useState<ErpSolution[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const theme = useTheme();
  const { api, isLoading, error } = useApi();
  const { api: deleteApi, isLoading: deleteIsLoading, error: deleteError } = useApi();
  const { api: dataApi, error: dataError } = useApi();
  const [paymentprofile, setPaymentprofile] = useState<Paymentprofile>(newPaymentprofile());
  const [filteredPaymentprofiles, setFilteredPaymentprofiles] = useState<Paymentprofile[]>([]);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState<readonly GridRowId[]>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEditPaymentprofile, setOpenEditPaymentprofile] = useState(false);
  const apiRef = useRef<GridApiCommunity>(null);
  // const addMemberRef = useRef<MemberFormRef>(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    getData();
  }, []);

  const onItemChange = (changedItem: Paymentprofile) => {
    setPaymentprofiles((prevProfiles) => {
      const index = prevProfiles.findIndex(u => u.id === changedItem.id);
      if (index > -1) {
        return prevProfiles.map((user, i) => (i === index ? changedItem : user));
      } else {
        return [...prevProfiles, changedItem];
      }
    });

    setPaymentprofile(changedItem);
    setSelectedIds([changedItem.id]);
  }
  
  const getData = () => {
    api("/getPaymentProfiles", "GET").then((res) => {
      if (res?.statusCode === 200) {
        setPaymentprofiles(res.data);
      }
    });

    dataApi("/getOrderPrepaymentSolutions", "GET").then((res) => {
      if (res?.statusCode === 200) {
        setPrepaymentSolutions(res.data);
      }
    });

    dataApi("/getOrderErpSolutions", "GET").then((res) => {
      if (res?.statusCode === 200) {
        setErpSolutions(res.data);
      }
    });
  }

  useEffect(() => {
    handleSearch(searchText);
  }, [paymentprofiles]);

  const handleSearch = (searchText: string) => {
    const query = searchText.toLowerCase();
    setSearchText(query);

    const filteredData = paymentprofiles ? paymentprofiles.filter((row) =>
      mainColumns.some((column) => {

        // Use valueGetter if defined, otherwise use the field directly
        const value = column.valueGetter
          ? column.valueGetter(undefined as never, row, column, apiRef)
          : row[column.field];

        return String(value).toLowerCase().includes(query);
      })
    ) : [];

    setFilteredPaymentprofiles(filteredData);
  };

  const getPagePaymentprofiles = (page: number, pageSize: number) => {
    if (paymentprofiles.length === 0) {
      return [];
    }
    const sublist = paymentprofiles.slice(page * pageSize, (page + 1) * pageSize);
    console.log("returning members from index", page * pageSize, "-", (page + 1) * pageSize, "count", sublist.length)
    return sublist;
  }

  const handleRowSelectionModelChange = (ids: number[]) => {
    setSelectedIds(ids);

    if (ids.length > 0) {
      let selectedPaymentprofile = paymentprofiles.find(u => u.id === ids[0]);
      setPaymentprofile(selectedPaymentprofile);
    } else {
      setPaymentprofile(newPaymentprofile());
    }
  }

  const handleRowDoubleClick = (row) => {
    let ids = [row.id];
    handleRowSelectionModelChange(ids);
    setOpenEditPaymentprofile(true);
  }

  const handleDelete = (hardDelete: boolean) => {
    alert("delete not implemented");
  }

  return (
    <>
      <BaseModal
        open={openEditPaymentprofile}
        title={paymentprofile.id > 0 ? "Rediger betalingsmåte" : "Ny betalingsmåte"}
        onClose={() => { setOpenEditPaymentprofile(false) }}
        closeOnBackdropClick={false}
        showButtons={false}
      >
        <PaymentprofileOverview onClose={() => setOpenEditPaymentprofile(false) } item={paymentprofile} onItemChange={onItemChange} PrepaymentSolutions={PrepaymentSolutions} ErpSolutions={ErpSolutions}/>
      </BaseModal>
      <HardDeleteDialog
        open={openDelete}
        bodyText={`Er du sikker på at du vil slette ${paymentprofile.name} ? Trykk på "SLETT" for å bekrefte.`}
        includeHardDelete={true}
        loading={deleteIsLoading}
        errorMessage={deleteError}
        onClose={() => setOpenDelete(false)}
        onConfirm={(hardDelete) => handleDelete(hardDelete)}
      />

      <StickyAlert message={error ? error : dataError ? dataError : null} severity="error" />

      <Box sx={{ m: 1, mt: 3, p: { xs: 1, md: 3 }, pt: 0, width: "100%" }}>
        <Paper elevation={3} sx={{ borderRadius: "4px 4px 0 0" }}>
          <EnhancedTableToolbar
            title={"Betalingsmåter"}
            numSelected={openEditPaymentprofile || selectedIds.length > 0 ? 1 : 0}
            selectTitle={paymentprofile.id ? `${paymentprofile.name}` : "Ny bruker"}
            onAdd={() => setOpenEditPaymentprofile(true)}
            onEdit={() => setOpenEditPaymentprofile(true)}
            onCancel={() => { setOpenEditPaymentprofile(false); setSelectedIds([]); setPaymentprofile(newPaymentprofile()) }}
            onDelete={() => setOpenDelete(true)}
            onSearch={handleSearch}
            onRefresh={getData}
          />
        </Paper>
        {/* {openEditPaymentprofile ?
          <>
            <PaymentprofileOverview paymentprofile={paymentprofile} onClose={() => setOpenEditPaymentprofile(false)} />
          </> : */}
          <ScrollableDataGrid
            height={700}
            rows={filteredPaymentprofiles}
            columns={mainColumns ? highlightedGridColumns(mainColumns, searchText, apiRef) : []}
            loading={isLoading}
            onPageRequest={getPagePaymentprofiles}
            checkboxSelection
            disableMultipleRowSelection
            onRowSelectionModelChange={(ids) => handleRowSelectionModelChange(ids)}
            onRowDoubleClick={(row) => handleRowDoubleClick(row)}
            rowSelectionModel={selectedIds}
            keepNonExistentRowsSelected={true}
          />
        {/* } */}
      </Box>

      {/* THIS MIGHT BE A SOLUTION TO INFINITE SCROLLING  */}
      {/* <Box sx={{ m: 1, p: 3, height: 700, pt: 0, width: "100%" }}>
        <VirtualizedDataGrid
          rows={filteredPaymentprofiles}
          columns={mainColumns ? highlightedGridColumns(mainColumns, searchText, apiRef) : []}
          loading={isLoading}
          theme={theme}
        />
      </Box> */}
    </>
  )
}